import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';


const sections = [
  { id: 'what-is-tarot', title: 'What is Tarot Card Reading?' },
  { id: 'tarot-decks', title: 'Tarot Card Decks' },
  { id: 'spreads', title: 'Tarot Card Spreads' },
  { id: 'reading-types', title: 'Reading for Yourself vs. Others' },
  { id: 'reading-space', title: 'Setting Up Your Reading Space' },
  { id: 'reversed-cards', title: 'About Reversed Cards' },
  { id: 'arcanas', title: 'About the Arcanas' },
  { id: 'minor-suits', title: 'About the Minor Card Suits' },
  { id: 'shadow-themes', title: 'About Shadow Themes' },
  { id: 'interpretation', title: 'How to Interpret Cards' },
  { id: 'myths', title: 'Common Myths' },
  { id: 'learn-more', title: 'Learn More' }
];

const Tarot101: React.FC = () => {
  const [isTocOpen, setIsTocOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto"
      >
        <div className="mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-ink dark:text-parchment mb-4">
            Tarot Reading 101
          </h1>
          <p className="text-lg text-ink dark:text-parchment/60">
            Welcome to your introduction to tarot! Let's explore this beautiful tool for insight and self-discovery together.
          </p>
        </div>

        <div className="px-0 md:px-2">
          {/* Table of Contents */}
          <div className="mb-12 bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
            <div className="p-6 flex items-center justify-between">
              <h2 className="text-xl font-display font-semibold text-sun dark:text-ember">
                Table of Contents
              </h2>
              <button
                onClick={() => setIsTocOpen(!isTocOpen)}
                className="p-1 hover:bg-vellum dark:hover:bg-shadow/40 rounded-full transition-colors"
                aria-label={isTocOpen ? 'Collapse table of contents' : 'Expand table of contents'}
              >
                <span className="text-sun dark:text-ember text-lg">{isTocOpen ? '−' : '+'}</span>
              </button>
            </div>

            <motion.div
              initial={false}
              animate={{
                height: isTocOpen ? 'auto' : 0,
                opacity: isTocOpen ? 1 : 0
              }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="text-left text-sun dark:text-ember hover:text-sun dark:hover:text-ember transition-colors"
                    >
                      {section.title}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          <div className="space-y-12">
            <section id="what-is-tarot">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                What is Tarot Card Reading?
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Today, tarot is a powerful tool for self-reflection, insight, and personal growth. Think of it as a mirror that helps you explore different aspects of your life and tap into your inner wisdom.
                </p>
                <p>
                  While tarot's exact origins are debated, it emerged in 15th century Europe as playing cards and evolved into the spiritual tool we know today. The most influential deck, the Rider-Waite-Smith, was published in 1909 and set the standard for modern tarot imagery.
                </p>
              </div>
            </section>

            <section id="tarot-decks">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                Tarot Card Decks
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Traditional decks like the Rider-Waite-Smith use rich symbolism that's stood the test of time. These classic decks are great for learning because most guides reference their imagery.
                </p>
                <p>
                  Modern decks come in countless styles, from minimalist to manga-inspired. Choose one that speaks to you - the best deck is one that resonates with your intuition and style.
                </p>
              </div>
            </section>

            <section id="spreads">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                Tarot Card Spreads
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  A spread is how you lay out your cards for a reading. Each position in the spread has a specific meaning, like "past influences" or "possible outcomes."
                </p>
                <p>
                  Setting up a spread is simple: shuffle your cards while focusing on your question, then draw and place them in the positions for your chosen spread. Start with simple spreads like single card or three-card readings before trying more complex layouts.
                </p>
              </div>
            </section>

            <section id="reading-types">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                Reading for Yourself vs. Others
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Reading for yourself is intimate and personal - it's about exploring your own path and inner wisdom. It's perfectly fine to interpret cards differently than traditional meanings when reading for yourself.
                </p>
                <p>
                  When reading for others, focus on creating a safe, supportive space. Be clear that you're sharing insights and possibilities, not predicting fixed outcomes. Let the querent (person receiving the reading) draw their own conclusions from the guidance offered.
                </p>
              </div>
            </section>

            <section id="reading-space">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                Setting Up Your Reading Space
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Your reading space should feel peaceful and focused. This might mean lighting a candle, playing soft music, or simply clearing your desk - whatever helps you feel centered and present.
                </p>
                <p>
                  Some readers like to use crystals, incense, or other tools, but these are completely optional. The most important thing is that you feel comfortable and can focus on your cards and intuition.
                </p>
              </div>
            </section>

            <section id="reversed-cards">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                About Reversed Cards
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Reversed cards (appearing upside down) aren't necessarily negative - they often suggest internal energies, blocked potential, or areas needing attention.
                </p>
                <p>
                  Working with reversals is optional. Many readers choose not to use them, especially when starting out. Trust your intuition about whether to include reversals in your practice.
                </p>
              </div>
            </section>

            <section id="arcanas">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                About the Arcanas
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  The Major Arcana (22 cards) represents life's big themes and spiritual lessons. These cards often indicate significant life events or major personal growth.
                </p>
                <p>
                  The Minor Arcana (56 cards) deals with day-to-day situations and practical matters. These cards help us understand the details and nuances of our daily experiences.
                </p>
              </div>
            </section>

            <section id="minor-suits">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                About the Minor Card Suits
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <ul className="space-y-3">
                  <li><strong>Wands:</strong> Represent fire energy - passion, creativity, action, and spiritual growth</li>
                  <li><strong>Cups:</strong> Represent water energy - emotions, relationships, intuition, and inner feelings</li>
                  <li><strong>Swords:</strong> Represent air energy - thoughts, communication, conflict, and mental clarity</li>
                  <li><strong>Pentacles:</strong> Represent earth energy - material world, work, money, and physical well-being</li>
                </ul>
              </div>
            </section>

            <section id="shadow-themes">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                About Shadow Themes
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Shadow themes in tarot represent the deeper, sometimes challenging aspects of a card's energy. These aren't negative per se, but rather invite us to explore the full spectrum of human experience.
                </p>
                <p>
                  When a card appears reversed or its shadow aspects are highlighted, it often points to:
                </p>
                <ul className="space-y-3 pl-5 list-disc">
                  <li>Internal rather than external manifestations of the card's energy</li>
                  <li>Areas where we might be resisting growth or change</li>
                  <li>Unconscious patterns that need attention</li>
                  <li>Opportunities for deeper self-awareness and healing</li>
                </ul>
                <p>
                  Working with shadow themes helps us embrace both light and dark aspects of ourselves, leading to more balanced and authentic growth.
                </p>
              </div>
            </section>

            <section id="interpretation">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                How to Interpret Cards
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Trust your intuition first! Notice your immediate reactions to the cards - what feelings, memories, or ideas come up? These personal insights are valuable.
                </p>
                <p>
                  Use our card guide as a reference, not a rulebook. Traditional meanings provide a foundation, but your intuitive understanding matters most.
                </p>
                <p>
                  Research and learn from different sources, but remember - tarot is a personal journey. The more you practice, the more confident you'll become in your own interpretation style.
                </p>
              </div>
            </section>

            <section id="myths">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                Common Myths
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Let's clear up some common misconceptions about tarot:
                </p>
                <ul className="space-y-3">
                  <li>
                    <strong>"Tarot is about predicting the future"</strong> - Tarot is more about understanding present energies and potential outcomes based on current paths.
                  </li>
                  <li>
                    <strong>"You need to be psychic to read tarot"</strong> - Anyone can learn to read tarot. It's about developing intuition and understanding symbolism.
                  </li>
                  <li>
                    <strong>"You must be gifted your first deck"</strong> - This is a modern myth. Choose and purchase your own deck that resonates with you.
                  </li>
                  <li>
                    <strong>"Reversed cards are always negative"</strong> - Reversed cards often suggest internal energies or areas needing attention, not necessarily negative outcomes.
                  </li>
                </ul>
              </div>
            </section>

            <section id="learn-more">
              <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                Learn More
              </h2>
              <div className="space-y-4 text-ink dark:text-parchment/60">
                <p>
                  Ready to dive deeper? Here are some ways to expand your practice:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Explore our <Link to="/guide/cards" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember">detailed card meanings</Link></li>
                  <li>Try different layouts with our <Link to="/guide/spreads" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember">spread guide</Link></li>
                  <li>Keep a <Link to="/history" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember">tarot journal</Link> to track your readings and insights</li>
                  <li>Join online tarot communities to share experiences</li>
                  <li>Read books from respected tarot authors</li>
                </ul>
                <p className="mt-4">
                  Remember, there's no "right" way to read tarot. The best practice is one that feels authentic to you and helps you connect with your intuition.
                </p>
              </div>
            </section>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Tarot101;